// The heads-up display: the player frame (bottom left: portrait, level, health, mana, stamina, exp), the menu buttons with
// your money and ping (bottom right), toasts, the zone banner and the F3 debug overlay. (The zone's name and the time
// are on the minimap, windows' Minimap; your coordinates on the world map, M; who's online on the player list, Tab.)
import { Widget, IconButton, widget, characterIcon, moneyView } from '../ui/compose';
import type { Frame } from './types';
import { button } from './items';
import { BuffKind } from '../../../shared/src/rules/buffs';

/** Each buff's icon in the UI skin (generate_textures.py). */
const BUFF_ICONS: Record<number, string> = { [BuffKind.Rekindled]: 'buff_rekindled', [BuffKind.Burning]: 'buff_burning' };

interface Notice { text: string; color: string; t: number; el: Widget }

/** What the menu buttons open (the game screen routes them). */
export type HudMenu = 'bag' | 'character' | 'talents' | 'quests' | 'friends' | 'map' | 'settings';

const MENU: [HudMenu, string, string][] = [
  ['bag', 'bag', 'Bag (B)'], ['character', 'character', 'Character (C)'], ['talents', 'talents', 'Talents (N)'], ['quests', 'quests', 'Quest journal (J)'],
  ['friends', 'friends', 'Friends (O)'], ['map', 'map', 'World map (M)'], ['settings', 'settings', 'Settings'],
];

export class Hud {
  readonly root: Widget;
  private readonly hpFill = widget('box', { class: 'bar-fill' });
  private readonly hpText = widget('box', { class: 'bar-text' });
  private readonly mpFill = widget('box', { class: 'bar-fill', style: { background: '#3c84d8' } });
  private readonly mpText = widget('box', { class: 'bar-text' });
  private readonly stFill = widget('box', { class: 'bar-fill' });
  private readonly stText = widget('box', { class: 'bar-text' });
  private readonly stBar: Widget;
  private readonly expFill = widget('box', { class: 'bar-fill' });
  private readonly expText = widget('box', { class: 'bar-text' });
  private readonly expBar: Widget;
  private readonly name = widget('box', { class: 'name' });
  /** A mark per aura from the party on us. */
  private readonly auras = widget('box', { class: 'row', style: { gap: '2px', alignItems: 'center' } });
  private aurasKey = '';
  private readonly level = widget('box', { class: 'level' });
  private readonly face = characterIcon(0, 32);
  private faceKey = '';
  private readonly money = widget('text');
  private moneyShown = -1;
  private readonly ping = widget('box', { class: 'ping' }, ...[0, 1, 2, 3].map(i => widget('tick', { style: { height: `${(i + 1) * 25}%` } })));
  private readonly pingText = widget('text', { class: 'small dim' });
  private readonly notices = widget('box', { class: 'notices' });
  private readonly banner = widget('box', { class: 'banner' });
  /** Buffs and debuffs on us, right of the player frame: an icon each, with the seconds left under it. */
  private readonly buffs = widget('box', { class: 'row', style: { gap: '2px', alignItems: 'flex-end' } });
  private buffsKey = '';
  private buffTimes: Widget[] = [];
  /** Dead where we fell: the screen with the Release Corpse button (the game screen mounts it over everything). */
  readonly deathScreen: Widget;
  /** A ghost: where to go and what to do there. */
  private readonly ghostHint = widget('box', { class: 'banner', style: { top: '10%' } });
  private readonly ghostWhere = widget('box', { class: 'dim' });
  private readonly debug = widget('box', { class: 'debug small' });
  readonly left = widget('box', { class: 'hud-left' });
  /** The bottom left corner: the chat (the game screen puts it here) over the player frame. */
  readonly bottomLeft: Widget;
  private noticeList: Notice[] = [];
  private bannerAt = -100;
  private fps = 60;
  private kbIn = 0;
  private kbOut = 0;
  private lastBytes = { in: 0, out: 0, t: 0 };
  showDebug = false;
  private debugAt = -Infinity;
  private debugLines: Widget[] = [];

  constructor(onMenu: (m: HudMenu) => void, onRelease: () => void) {
    const hp = widget('box', { class: 'bar' }, this.hpFill, this.hpText);
    const mp = widget('box', { class: 'bar', title: 'Mana: staves spend it' }, this.mpFill, this.mpText);
    this.stBar = widget('box', { class: 'bar' }, this.stFill, this.stText);
    this.expBar = widget('box', { class: 'bar exp' }, this.expFill, widget('box', { class: 'exp-ticks' }), this.expText);
    const portrait = widget('box', { class: 'well portrait' }, this.face, this.level);
    const frame = widget('box', { class: 'panel player-frame' }, portrait,
      widget('box', { class: 'bars grow' }, widget('box', { class: 'row', style: { alignItems: 'center' } }, this.name, widget('box', { class: 'grow' }), this.auras),
        hp, mp, this.stBar, this.expBar));
    this.bottomLeft = widget('box', { class: 'hud-bottom-left' }, widget('box', { class: 'row', style: { gap: '3px', alignItems: 'flex-end' } }, frame, this.buffs));
    this.deathScreen = widget('box', { class: 'screen', style: { display: 'none', background: 'rgba(40,6,8,0.55)', zIndex: '45' } },
      widget('box', { class: 'panel col', style: { alignItems: 'center', gap: '4px', padding: '8px', minWidth: '200px' } },
        widget('box', { class: 'big', style: { color: '#e05a4a' } }, 'You died'),
        widget('box', { class: 'small dim' }, 'Your ghost rises at the nearest graveyard.'),
        widget('box', { class: 'small dim' }, 'Run back to your corpse and hold E to rekindle.'),
        widget('box', { class: 'buttons' }, button('Release Corpse', onRelease))));
    this.ghostHint.append(widget('box', { class: 'big', style: { color: '#a8d8ff' } }, 'You are a ghost'), this.ghostWhere);
    const menu = widget('box', { class: 'hud-menu' },
      ...MENU.map(([m, icon, title]) => new IconButton(icon, title, () => onMenu(m))),
      widget('box', { class: 'panel info' }, this.money, widget('text', { class: 'row', style: { gap: `2px` } }, this.ping, this.pingText)));
    this.root = widget('box', { class: 'hud passthrough' },
      widget('box', { class: 'hud-center' }, this.notices),
      this.bottomLeft, menu, this.banner, this.ghostHint,
      widget('box', { class: 'hud-left-stack' }, this.debug, this.left));
  }

  notice(kind: string, text: string, now: number): void {
    const color = kind === 'success' ? '#aae678' : kind === 'error' ? 'var(--error)' : 'var(--text)';
    const el = widget('box', { class: 'notice', style: { color } }, text);
    this.notices.append(el);
    this.noticeList.push({ text, color, t: now, el });
    while (this.noticeList.length > 4) this.noticeList.shift()!.el.dispose();
  }

  showBanner(zone: string, server: string, now: number): void {
    this.banner.replaceChildren(widget('box', { class: 'big' }, zone), widget('box', { class: 'dim' }, server));
    this.bannerAt = now;
  }

  update(f: Frame, dt: number, extras: { zoom: number; hideBanner: boolean }): void {
    const hud = f.hud, now = f.t;

    // The portrait: redrawn when the look changes (armour), sized to the frame.
    const size = 32, faceKey = `${f.me.appearance}|${f.me.armor.join(',')}|${size}`;
    if (faceKey !== this.faceKey) {
      this.faceKey = faceKey;
      this.face.setAppearance(f.me.appearance, 0, 0, f.me.armor);
      this.face.style.width = this.face.style.height = `${size}px`;
    }
    this.name.textContent = f.me.name;
    const aurasKey = JSON.stringify(hud.auras);
    if (aurasKey !== this.aurasKey) {
      this.aurasKey = aurasKey;
      this.auras.replaceChildren(...hud.auras.map(a => widget('box', { title: `${a.name} (party aura): ${a.text}`,
        style: { width: '6px', height: '6px', background: '#7fd99a', border: '1px solid #1c3a24' } })));
    }
    const buffsKey = hud.buffs.map(b => b.kind).join(',');
    if (buffsKey !== this.buffsKey) {
      this.buffsKey = buffsKey;
      this.buffTimes = hud.buffs.map(() => widget('text', { class: 'small', style: { textOutline: '1' } }));
      this.buffs.replaceChildren(...hud.buffs.map((b, i) => widget('box', { class: 'col', style: { alignItems: 'center', gap: '0px', pointerEvents: 'auto' },
          title: `${b.name}${b.debuff ? ' (debuff)' : ''}: ${b.description}` },
        widget('box', { class: 'well', style: { width: '18px', height: '18px', padding: '1px', border: `1px solid ${b.debuff ? '#c8343a' : '#e8c068'}` } },
          widget('box', { class: 'icon16', style: { backgroundImage: `var(--img-${BUFF_ICONS[b.kind] ?? 'diamond'})` } })),
        this.buffTimes[i])));
    }
    hud.buffs.forEach((b, i) => { if (this.buffTimes[i]) this.buffTimes[i].textContent = `${Math.ceil(b.seconds)}s`; });
    // Death: the release screen while we lie where we fell; a ghost's hint while we run back.
    this.deathScreen.style.display = f.life.state === 'dead' ? '' : 'none';
    this.ghostHint.style.display = f.life.state === 'ghost' ? '' : 'none';
    if (f.life.state === 'ghost') this.ghostWhere.textContent = f.life.corpseHere
      ? 'Return to your corpse and hold E to rekindle.' : `Your corpse lies in ${f.life.corpseMap || 'another place'}. Hold E beside it to rekindle.`;
    this.level.textContent = String(hud.level);
    this.level.style.color = hud.unspent > 0 ? 'var(--good)' : '';
    this.level.title = hud.unspent > 0 ? `${hud.unspent} talent point${hud.unspent === 1 ? '' : 's'} to spend (N)` : `Level ${hud.level}`;

    const hp = hud.maxHp > 0 ? hud.hp / hud.maxHp : 0;
    this.hpFill.style.width = `${Math.max(0, Math.min(1, hp)) * 100}%`;
    this.hpFill.style.background = hp <= 0.25 && Math.floor(now * 3) % 2 === 0 ? '#ff5a3c' : '#c8343a';
    this.hpText.textContent = `${hud.hp} / ${hud.maxHp}`;
    const mp = hud.maxMana > 0 ? hud.mana / hud.maxMana : 0;
    this.mpFill.style.width = `${Math.max(0, Math.min(1, mp)) * 100}%`;
    this.mpText.textContent = `${Math.floor(hud.mana)} / ${hud.maxMana}`;
    const sp = hud.stamina / hud.maxStamina;
    this.stFill.style.width = `${Math.max(0, Math.min(1, sp)) * 100}%`;
    this.stFill.style.background = hud.exhausted ? '#6e6a72' : '#d4a03a';
    this.stText.textContent = hud.exhausted ? 'Exhausted' : `${Math.floor(hud.stamina)} / ${hud.maxStamina}`;
    this.expFill.style.width = `${hud.next > 0 ? Math.min(1, hud.exp / hud.next) * 100 : 100}%`;
    this.expFill.style.background = hud.next > 0 ? '#8a52e0' : '#ffcd5a';
    this.expText.textContent = hud.next > 0 ? `XP ${hud.exp.toLocaleString()} / ${hud.next.toLocaleString()}` : 'max level';
    this.expBar.title = hud.next > 0 ? `${hud.next - hud.exp} exp to level ${hud.level + 1}` : '';

    if (hud.money !== this.moneyShown) {
      this.moneyShown = hud.money;
      this.money.replaceChildren(moneyView(hud.money));
    }
    const bars = hud.ping <= 0 ? 0 : hud.ping < 80 ? 4 : hud.ping < 150 ? 3 : hud.ping < 300 ? 2 : 1;
    Array.from(this.ping.widgets).forEach((el, i) => el.classList.toggle('on', i < bars));
    this.pingText.textContent = hud.ping > 0 ? `${hud.ping} ms` : '';

    // Toasts fade after 3 s, with a small pop when they appear.
    this.noticeList = this.noticeList.filter(n => {
      const age = now - n.t;
      if (age > 3) { n.el.dispose(); return false; }
      n.el.style.opacity = String(Math.max(0, Math.min(1, (3 - age) / 0.6)));
      n.el.style.transform = `translateY(${-Math.min(age, 0.25) * 16}px)`;
      return true;
    });

    const age = now - this.bannerAt;
    const alpha = age < 4 && !extras.hideBanner ? Math.max(0, Math.min(1, age < 0.5 ? age / 0.5 : (4 - age) / 1)) : 0;
    this.banner.style.opacity = String(alpha);

    this.fps = this.fps * 0.95 + (dt > 0 ? 1 / dt : 0) * 0.05;
    this.debug.style.display = this.showDebug ? '' : 'none';
    if (this.showDebug && now - this.debugAt >= 0.25) {
      this.debugAt = now;
      if (now - this.lastBytes.t >= 1) {
        const span = now - this.lastBytes.t;
        this.kbIn = (hud.bytesIn - this.lastBytes.in) / 1024 / span;
        this.kbOut = (hud.bytesOut - this.lastBytes.out) / 1024 / span;
        this.lastBytes = { in: hud.bytesIn, out: hud.bytesOut, t: now };
      }
      const lines = [
        `fps ${this.fps.toFixed(0)}  zoom ${extras.zoom}x`,
        `pos ${f.me.x.toFixed(1)}, ${f.me.y.toFixed(1)}`,
        `server tick ${hud.tick.toFixed(1)}`,
        `pending inputs ${hud.pending}  corrections ${hud.corrections}`,
        `chunks ${hud.chunks}  entities ${f.ents.length}  epoch ${hud.epoch}`,
        `net in ${this.kbIn.toFixed(1)} KB/s  out ${this.kbOut.toFixed(1)} KB/s  ping ${hud.ping} ms`,
      ];
      if (!this.debugLines.length) { this.debugLines = lines.map(t => widget('box', null, t)); this.debug.append(...this.debugLines); }
      else lines.forEach((text, i) => this.debugLines[i].textContent = text);
    }
  }
}
